import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FileText,
  Edit3,
  Trash2, 
  Search, 
  Plus, 
  X, 
  Mail, 
  Phone, 
  ChevronLeft,
  ChevronRight,
  Users
} from 'lucide-react';
import api from '../services/api';
import AdmissionForm from '../pages/AdmissionForm';
import StudentAvatar from './StudentAvatar';

const ClassesListView = ({ visaType, title }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const itemsPerPage = 10;

  const PREP_TYPES = ['Japanese', 'Korean', 'IELTS'];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const studentsRes = await api.get('/students');
      setStudents(studentsRes.data.filter(s => s.visa_type === visaType));
        if (!selectedClass) setSelectedClass('Japanese');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [visaType, selectedClass]);

  const handleDelete = async (student) => {
    const confirmDelete = window.confirm(`Are you sure you want to permanently delete ${student.full_name} and all their records? This cannot be undone.`);
    if (confirmDelete) {
      try {
        await api.delete(`/students/${student.id}`);
        alert('Student deleted successfully');
        fetchData();
      } catch (err) {
        alert(err.response?.data?.error || 'Error deleting student.');
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Dynamically determine relevant students for the selected class
  const classStudents = students.filter(s => s.preparation_classes?.includes(selectedClass));

  const filteredStudents = classStudents.filter(s => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      s.full_name.toLowerCase().includes(lowerTerm) ||
      s.email.toLowerCase().includes(lowerTerm) || 
      (s.phone && s.phone.includes(lowerTerm)) ||
      (s.passport_no && s.passport_no.toLowerCase().includes(lowerTerm))
    );
  });

  if (loading) return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading {visaType} Registry...</p>
      </div>
    </div>
  );

  const paginatedStudents = filteredStudents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">{title}</h1>
          <p className="text-sm text-gray-500 font-semibold uppercase tracking-widest flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span> 
            {visaType} VISA DEPARTMENT
          </p>
        </div>
        
        <div className="flex gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
               <Search className="absolute left-4 top-2.5 text-gray-400" size={18} />
               <input 
                  type="text" 
                  placeholder="Search class applicants..." 
                  className="input-field pl-12"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
               />
            </div>
            <button className="btn-primary whitespace-nowrap" onClick={() => setShowAdmissionForm(!showAdmissionForm)}>
                {showAdmissionForm ? <X size={18} /> : <Plus size={18} />}
                {showAdmissionForm ? 'Cancel' : 'Add Student'}
            </button>
        </div>
      </div>

      {showAdmissionForm && (
        <div className="animate-in slide-in-from-top-4 duration-300">
           <AdmissionForm 
              defaultVisaType={visaType}
              onSuccess={() => {
                  fetchData();
                  setShowAdmissionForm(false);
              }} 
              onCancel={() => setShowAdmissionForm(false)} 
           />
        </div>
      )}

      {!showAdmissionForm && (
        <>
            {/* Filter Tabs Section */}
            <div className="space-y-4 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 overflow-x-auto no-scrollbar pb-1">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter shrink-0">Preparation Class:</span>
                    <div className="flex gap-2">
                        {PREP_TYPES.map(type => (
                            <button
                                key={type}
                                onClick={() => { setSelectedClass(type); setCurrentPage(1); }}
                                className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all border shrink-0 ${
                                    selectedClass === type
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                                        : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {type}
                                <span className={`ml-2 px-1.5 py-0.5 rounded-lg text-[9px] ${selectedClass === type ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    {students.filter(s => s.preparation_classes?.includes(type)).length}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="card !p-0 overflow-hidden border-b-4 border-b-indigo-500 shadow-sm">
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[22%] min-w-[180px]">Student Info</th>
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[22%] min-w-[180px]">Contact Details</th>
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[13%] min-w-[110px]">Visa Pathway</th>
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[13%] min-w-[110px]">Destination</th>
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[12%] min-w-[110px]">Managed By</th>
                      <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedStudents.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-6 py-20 text-center text-gray-400 italic">
                            <Users className="mx-auto text-gray-100 mb-4" size={48} />
                            <p className="font-bold uppercase tracking-widest text-xs">No students found in this class segment</p>
                        </td>
                      </tr>
                    )}
                    {paginatedStudents.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <StudentAvatar photoUrl={s.photo_url} fullName={s.full_name} className="w-10 h-10 rounded-xl" />
                            <div>
                              <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{s.full_name}</p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter text-blue-900 bg-blue-50/50 inline-block px-1.5 rounded mt-0.5">
                                {s.passport_no || 'NO PASSPORT'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1 max-w-[220px]">
                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 truncate" title={s.email}>
                               <Mail size={12} className="text-gray-300 shrink-0" />
                               <span className="truncate">{s.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 truncate" title={s.phone}>
                               <Phone size={12} className="text-gray-300 shrink-0" />
                               <span className="truncate">{s.phone}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`status-badge ${s.visa_type === 'Student' ? 'status-active' : 'status-info'}`}>
                            {s.visa_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">📍</span>
                            <span className="text-sm font-bold text-blue-900">{s.abroad_study_country || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-xs font-bold text-gray-700">{s.creator_name || 'System'}</p>
                          <p className="text-[10px] text-gray-400">{new Date(s.created_at).toLocaleDateString()}</p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => navigate(`/students/${s.id}`)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                              title="View Profile"
                            >
                              <FileText size={18} />
                            </button>
                            <button
                              onClick={() => navigate(`/students/${s.id}?edit=true`)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                              title="Edit Student"
                            >
                              <Edit3 size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(s)}
                              className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                              title="Delete Student"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Section */}
              {totalPages > 1 && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredStudents.length)} of {filteredStudents.length}
                      </p>
                      <div className="flex gap-2">
                        <button 
                          disabled={currentPage === 1} 
                          onClick={() => setCurrentPage(prev => prev - 1)}
                          className="p-2 bg-white border border-gray-100 rounded-xl disabled:opacity-30 hover:shadow-sm transition-all"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center px-4 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-900">
                          {currentPage} / {totalPages}
                        </div>
                        <button 
                          disabled={currentPage === totalPages} 
                          onClick={() => setCurrentPage(prev => prev + 1)}
                          className="p-2 bg-white border border-gray-100 rounded-xl disabled:opacity-30 hover:shadow-sm transition-all"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                  </div>
              )}
            </div>
        </>
      )}
    </div>
  );
};

export default ClassesListView;
